import { Children, memo, useEffect, useMemo, useRef, useState } from "react";

import { StyledIconContainer, StyledSidebarArrow } from "../../Sidebar-styled";
import SidebarDropdown from "../SidebarDropdown/SidebarDropdown";

import { useClickOutside } from "../../../../../../hooks";
import { triggerGAEvent } from "../../../../../../utils/helper";
import { IconButton, Tooltip } from "../../../../../UI";
import {
  SectionInnerContainer,
  SectionInnerContainerItem,
  SectionItem,
  SectionItemContainer,
} from "./Section-styled";

const Section = ({
  onClick,
  expanded,
  title,
  Icon,
  children,
  options = [],
  className = "",
  optionsVisible = false,
  information = "",
  TrailingComponent,
  widgetName = "",
  isSelected,
  setIsSelected,
  disabled,
}) => {
  const [internalSelected, setInternalSelected] = useState(false);
  const selected =
    typeof isSelected === "boolean" ? isSelected : internalSelected;
  const setSelected = setIsSelected || setInternalSelected;
  const ref = useRef();

  useClickOutside(
    ref,
    () => {
      if (expanded || !selected || isSelected) return;
      setSelected(false);
    },
    ["click"]
  );

  useEffect(() => {
    if (selected) {
      setSelected(false);
    }
    return () => {};
  }, [expanded]);

  const isChildrenEmpty = useMemo(
    () => (children ? Children.count(children) === 0 : !children),
    [children]
  );

  const hasOptions = useMemo(() => {
    return options.length > 0 || !isChildrenEmpty;
  }, [options, children]);

  const showOptions = useMemo(
    () => (selected || optionsVisible) && hasOptions,
    [selected, hasOptions, optionsVisible]
  );

  const Options = options.map((option, index) => (
    <SectionInnerContainerItem
      selected={option.selected}
      onClick={!option.selected ? option.onClick : undefined}
      key={index}
    >
      <Tooltip
        wrapperStyles={{
          position: "absolute",
          width: "100%",
          height: "100%",
        }}
        placement="right"
        offset={16}
        blurred
        content={option.description}
        showOnHover
        disabled={!option.description}
      />
      <StyledIconContainer selected={option.selected}>
        {option.icon}
      </StyledIconContainer>
      <span>{option.title}</span>
    </SectionInnerContainerItem>
  ));

  return (
    <SectionItemContainer ref={ref} className={className} disabled={disabled}>
      <SectionItem
        onClick={() => {
          setSelected((prev) => !prev);
          if (onClick) {
            onClick();
          }

          if (widgetName && !selected) {
            triggerGAEvent({
              event: "widget",
              params: {
                widget_name: widgetName,
              },
            });
          }
        }}
        selected={selected}
      >
        <Tooltip
          wrapperStyles={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 2,
          }}
          content={!expanded ? title : information}
          placement="right"
          offset={16}
          showOnHover
          blurred
        >
          <div
            style={{
              width: "100%",
              height: "100%",
            }}
          ></div>
        </Tooltip>
        <IconButton selected={selected}>
          {Icon && <Icon selected={selected} />}
        </IconButton>
        {expanded ? <span className="text-elipsis">{title}</span> : ""}
        {expanded && hasOptions && !TrailingComponent && (
          <span style={{ marginLeft: "auto", marginRight: 3 }}>
            <StyledSidebarArrow isRotateç={selected ? selected : undefined} />
          </span>
        )}
        {TrailingComponent && <TrailingComponent />}
      </SectionItem>
      {expanded && (
        <SectionInnerContainer className={showOptions ? "" : "collapsed"}>
          {children || Options}
        </SectionInnerContainer>
      )}
      {showOptions && !expanded && (
        <SidebarDropdown options={options}>{children}</SidebarDropdown>
      )}
    </SectionItemContainer>
  );
};

export default memo(Section);
